import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import centralizedConfig from 'utils/centralizedConfig';
import {
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe with publishable key from centralized configuration (no hardcoded fallback)
// Load Stripe lazily to avoid initialization errors at module load time
const publishableKey = (typeof window !== 'undefined' && window._env_ && window._env_.STRIPE_PUBLISHABLE_KEY) || 
                       (typeof centralizedConfig !== 'undefined' && centralizedConfig.get('STRIPE_PUBLISHABLE_KEY')) || 
                       null;
// Don't call loadStripe at module level - will be loaded lazily in component
// eslint-disable-next-line no-unused-vars -- reserved for Stripe Elements
let stripePromise = null;

const PayPalSetupForm = ({ setupIntent, onSubmit, onCancel, loading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !setupIntent) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the setup intent with PayPal
      const { error, setupIntent: confirmedSetupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: new URL('/?tab=cost-pools', window.location.origin).toString(),
        },
        redirect: 'if_required',
      });

      if (error) {
        setError(error.message);
        setProcessing(false);
        return;
      }

      // Call the parent component's onSubmit with the confirmed setup intent
      await onSubmit(confirmedSetupIntent);
    } catch (err) {
      setError('An unexpected error occurred');
      setProcessing(false);
    }
  };

  return (
    <div className="paypal-setup-form">
      <div className="paypal-info">
        <h4>PayPal Setup</h4>
        <p>You will be redirected to PayPal to authorize the connection to your account.</p>
        <p>This is a secure process handled by Stripe and PayPal.</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button 
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={processing || loading}
        >
          Cancel
        </button>
        <button 
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!stripe || processing || loading}
        >
          {processing ? 'Redirecting to PayPal...' : 'Continue to PayPal'}
        </button>
      </div>

      <style>{`
        .paypal-setup-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .paypal-info {
          padding: 20px;
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
        }

        .paypal-info h4 {
          margin: 0 0 12px 0;
          color: var(--text-primary);
          font-size: 1.1em;
        }

        .paypal-info p {
          margin: 0 0 8px 0;
          color: var(--text-secondary);
          font-size: 0.9em;
          line-height: 1.4;
        }

        .paypal-info p:last-child {
          margin-bottom: 0;
        }

        .error-message {
          padding: 12px;
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 6px;
          color: #721c24;
          font-size: 0.9em;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid var(--border-light);
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #5a6268;
        }

        .btn-primary {
          background-color: var(--primary-color);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: var(--primary-color-dark);
        }
      `}</style>
    </div>
  );
};

const PayPalSetupFormWrapper = ({ setupIntent, onSubmit, onCancel, loading }) => {
  const [currentStripePromise, setCurrentStripePromise] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  // Initialize Stripe lazily when component mounts
  useEffect(() => {
    if (!publishableKey) {
      setStripeError('Stripe is not configured. Set STRIPE_PUBLISHABLE_KEY in local.settings.json or inject via env-config.');
      return;
    }

    let isMounted = true;
    const initStripe = async () => {
      try {
        setStripeError(null);
        const promise = loadStripe(publishableKey);
        setCurrentStripePromise(promise);
        // Verify it resolves
        await promise;
      } catch (err) {
        if (isMounted) {
          console.error('Stripe initialization error:', err);
          setStripeError(`Failed to load Stripe.js: ${err?.message || 'Unknown error'}`);
          setCurrentStripePromise(null);
        }
      }
    };

    initStripe();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!publishableKey || stripeError) {
    return (
      <div className="error-message">
        {stripeError || 'Stripe is not configured. Set STRIPE_PUBLISHABLE_KEY in local.settings.json or inject via env-config.'}
      </div>
    );
  }

  if (!currentStripePromise) {
    return (
      <div className="loading-message">
        Loading payment form...
      </div>
    );
  }

  return (
    <Elements stripe={currentStripePromise}>
      <PayPalSetupForm 
        setupIntent={setupIntent} 
        onSubmit={onSubmit} 
        onCancel={onCancel} 
        loading={loading} 
      />
    </Elements>
  );
};

export default PayPalSetupFormWrapper;
